import { getPrerenderWidth } from "@/helpers/prerenderWidth"
import { useSyncExternalStore } from "react"

/** antd's Grid breakpoints: `xs` below 576 px; `sm`, `md`, `lg`, `xl`, `xxl` from 576, 768, 992, 1200, 1600 px up. */
export type ScreenMap = { xs: boolean; sm: boolean; md: boolean; lg: boolean; xl: boolean; xxl: boolean }

export const screensForWidth = (width: number): ScreenMap => ({
  xs: width < 576,
  sm: width >= 576,
  md: width >= 768,
  lg: width >= 992,
  xl: width >= 1200,
  xxl: width >= 1600,
})

// One object per set of values, shared by both snapshots: useSyncExternalStore compares them by identity, and a
// different object after hydration — same values or not — makes React redraw the component at once, which throws
// away a nested Suspense boundary that is not hydrated yet (error #421; the home page's 3D hero).
const snapshots = new Map<string, ScreenMap>()
const snapshotFor = (width: number): ScreenMap => {
  const screens = screensForWidth(width)
  const key = Object.values(screens).join()
  if (!snapshots.has(key)) snapshots.set(key, screens)
  return snapshots.get(key) as ScreenMap
}

const getWindowScreens = () => snapshotFor(window.innerWidth)
const getPrerenderScreens = () => snapshotFor(getPrerenderWidth())

const subscribe = (onChange: () => void) => {
  window.addEventListener("resize", onChange)
  return () => window.removeEventListener("resize", onChange)
}

/**
 * The window's breakpoints, in the shape of antd's Grid.useBreakpoint(). antd's are empty until the component has
 * mounted; these are right on the first render, and on the server and while a prerendered page hydrates they are those
 * of the width the page was rendered at (helpers/prerenderWidth.ts), so the page keeps its layout when React takes it
 * over. Use this one, not antd's, in anything the public pages render.
 */
export const useBreakpoint = (): ScreenMap => useSyncExternalStore(subscribe, getWindowScreens, getPrerenderScreens)
